import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  ChevronDown,
  Check,
  Eye,
  Pencil,
  Trash2,
} from "lucide-react";

interface ContractItem {
  id: string;
  stt: number;
  contractName: string;
  implementingUnit: string;
  serviceTypes: {
    cgcn: boolean;
    dvkt: boolean;
    tuvan: boolean;
    dtcmnv: boolean;
    dichvukhac: boolean;
  };
  contractValue: string;
  implementationTime: {
    start: string;
    end: string;
  };
  signingPartner: string;
  notes: string;
}

const mockData: ContractItem[] = [
  {
    id: "1",
    stt: 1,
    contractName: "HĐ 1 »",
    implementingUnit: "Viện Nước, Tưới Tiêu và Môi trường",
    serviceTypes: {
      cgcn: false,
      dvkt: true,
      tuvan: false,
      dtcmnv: false,
      dichvukhac: false,
    },
    contractValue: "90 triệu",
    implementationTime: {
      start: "24/03/2022",
      end: "24/03/2022",
    },
    signingPartner: "",
    notes: "",
  },
];

const Contracts: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [openMenuContractId, setOpenMenuContractId] = useState<string | null>(
    null
  );
  const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">(
    "bottom"
  );
  const [buttonPosition, setButtonPosition] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Delete modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteContractId, setDeleteContractId] = useState<string | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuContractId) {
        const currentRef = dropdownRefs.current[openMenuContractId];
        if (currentRef && !currentRef.contains(event.target as Node)) {
          setOpenMenuContractId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openMenuContractId && buttonPosition) {
        // Update position on scroll to keep menu attached to button
        const button = document.querySelector(
          `[data-contract-id="${openMenuContractId}"]`
        ) as HTMLButtonElement;
        if (button) {
          const rect = button.getBoundingClientRect();
          setButtonPosition({
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
          });
        }
      }
    };

    if (openMenuContractId) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuContractId, buttonPosition]);

  const handleDeleteConfirm = () => {
    if (deleteContractId) {
      console.log("Deleting contract:", deleteContractId);
      // Here you would implement the actual delete logic
      setShowDeleteConfirm(false);
      setDeleteContractId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteContractId(null);
  };

  const handleViewDetail = (contractId: string) => {
    console.log("Viewing details for contract:", contractId);
    navigate(`/contracts/detail/${contractId}`);
  };

  const handleEdit = (contractId: string) => {
    console.log("Editing contract:", contractId);
    navigate(`/contracts/edit/${contractId}`);
  };

  return (
    <PageShell title="Hợp đồng - dịch vụ KH&CN">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Hợp đồng - dịch vụ KH&CN
          </h1>
          <button
            onClick={() => navigate("/add-new-contract")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Hợp đồng</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm hợp đồng"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filters */}
            <div className="flex space-x-4">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Chọn đơn vị -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Loại hình -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Chọn năm chuyển giao -</option>
              </select>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* First Header Row */}
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Tên hợp đồng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48">
                    Đơn vị thực hiện
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200"
                    colSpan={5}
                  >
                    Loại hình dịch vụ KH&CN
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Giá trị HĐ (triệu đồng)
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200"
                    colSpan={2}
                  >
                    Thời gian thực hiện
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Đối tác ký
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Ghi chú
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-20">
                    Chức năng
                  </th>
                </tr>
                {/* Second Header Row */}
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-48"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    CGCN
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    DVKT
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Tư vấn
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    ĐT CMNV
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Dịch vụ khác
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Bắt đầu
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Kết thúc
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20"></th>
                  <th className="px-2 py-2 text-left text-xs font-bold text-black uppercase tracking-wider w-20"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-8">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-40">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                        {item.contractName}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-48">
                      {item.implementingUnit}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16 text-center">
                      {item.serviceTypes.cgcn && (
                        <Check className="h-4 w-4 mx-auto text-black" />
                      )}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16 text-center">
                      {item.serviceTypes.dvkt && (
                        <Check className="h-4 w-4 mx-auto text-black" />
                      )}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16 text-center">
                      {item.serviceTypes.tuvan && (
                        <Check className="h-4 w-4 mx-auto text-black" />
                      )}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16 text-center">
                      {item.serviceTypes.dtcmnv && (
                        <Check className="h-4 w-4 mx-auto text-black" />
                      )}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16 text-center">
                      {item.serviceTypes.dichvukhac && (
                        <Check className="h-4 w-4 mx-auto text-black" />
                      )}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.contractValue}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      {item.implementationTime.start}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      {item.implementationTime.end}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.signingPartner || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.notes || ""}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap border-r border-gray-200 w-20">
                      <div
                        className="relative inline-block"
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          data-contract-id={item.id}
                          className="bg-[#337ab7] text-white px-2 py-1 rounded text-xs font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-1"
                          onClick={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            const rect = target.getBoundingClientRect();
                            const estimatedMenuHeight = 150; // approx height for 3 items with padding
                            const spaceBelow = window.innerHeight - rect.bottom;
                            // More aggressive flipping - flip if less than 150px space below
                            const willOverflowBottom = spaceBelow < 150;
                            setMenuPlacement(
                              willOverflowBottom ? "top" : "bottom"
                            );
                            setButtonPosition({
                              left: rect.left,
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                            });
                            setOpenMenuContractId(
                              openMenuContractId === item.id ? null : item.id
                            );
                          }}
                        >
                          <span>Chọn</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        {openMenuContractId === item.id &&
                          createPortal(
                            <div
                              className={
                                `fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[99999] ` +
                                (menuPlacement === "bottom"
                                  ? "mt-2"
                                  : "bottom-full mb-2")
                              }
                              onMouseDown={(e) => {
                                // Prevent the global mousedown outside handler from closing
                                // the menu before the anchor click can navigate
                                e.stopPropagation();
                              }}
                              style={
                                buttonPosition
                                  ? {
                                      left: `${buttonPosition.right - 200}px`, // 200px = w-48 + 16px offset
                                      top:
                                        menuPlacement === "bottom"
                                          ? `${buttonPosition.bottom + 0}px`
                                          : `${buttonPosition.top - 150}px`,
                                      backgroundColor: "white",
                                      zIndex: 99999,
                                    }
                                  : {}
                              }
                            >
                              <div className="py-1 bg-white">
                                <button
                                  onClick={() => {
                                    handleViewDetail(item.id);
                                    setOpenMenuContractId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Eye className="h-4 w-4 text-blue-600" />
                                  <span>Chi tiết</span>
                                </button>
                                <button
                                  onClick={() => {
                                    handleEdit(item.id);
                                    setOpenMenuContractId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Pencil className="h-4 w-4 text-amber-600" />
                                  <span>Sửa</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setDeleteContractId(item.id);
                                    setShowDeleteConfirm(true);
                                    setOpenMenuContractId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                  <span>Xóa</span>
                                </button>
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Xác nhận xóa
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có chắc chắn muốn xóa hợp đồng này không? Hành động này
                  không thể hoàn tác.
                </p>
                <div className="flex space-x-3 justify-center">
                  <button
                    onClick={handleDeleteCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Xóa
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default Contracts;
